import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, MessageCircle, MapPin, Navigation, Linkedin, Twitter, Youtube } from "lucide-react";
import { Logo } from "@/components/Logo";
import { SITE } from "@/config/site";
import api from "@/lib/api";
import { trackEvent } from "@/lib/analytics";

export default function Footer() {
  const [categories, setCategories] = useState([]);
  const [settings, setSettings] = useState({});

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  return (
    <footer className="border-t border-white/10 bg-deep text-slate-400">
      <div className="mx-auto max-w-7xl px-4 pb-10 pt-16 sm:px-6 lg:px-8 lg:pt-20">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <Link to="/" aria-label="STJ Innovations home" data-testid="footer-logo-link">
              <Logo light large />
            </Link>
            <p className="mt-5 max-w-sm text-sm leading-relaxed text-slate-400">
              {SITE.subtitle}. Designed and manufactured in India for pharmaceutical, healthcare and controlled-environment applications worldwide.
            </p>
            <p className="mt-5 font-mono text-[11px] uppercase tracking-[0.22em] text-slate-500">
              {SITE.legalName}
            </p>
            <div className="mt-6 flex gap-3">
              <a href="https://www.linkedin.com/company/stjinnovations/" target="_blank" rel="noopener noreferrer"
                data-testid="footer-linkedin-link" aria-label="STJ Innovations on LinkedIn"
                onClick={() => trackEvent("social_click", { network: "linkedin" })}
                className="flex h-9 w-9 items-center justify-center border border-white/10 text-slate-400 transition-colors hover:border-accent hover:text-accent">
                <Linkedin size={15} />
              </a>
              {[Twitter, Youtube].map((Icon, i) => (
                <span key={i} data-testid={`footer-social-${i}`} title="Social profile — link to be added"
                  className="flex h-9 w-9 items-center justify-center border border-white/10 text-slate-500 transition-colors hover:border-accent hover:text-accent">
                  <Icon size={15} />
                </span>
              ))}
            </div>
          </div>

          <div className="lg:col-span-3">
            <h3 className="eyebrow eyebrow-light mb-5">Product Categories</h3>
            <ul className="space-y-2.5 text-sm">
              {categories.slice(0, 9).map((c) => (
                <li key={c.slug}>
                  <Link to={`/products/category/${c.slug}`} data-testid={`footer-category-${c.slug}`} className="transition-colors hover:text-white">
                    {c.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-2">
            <h3 className="eyebrow eyebrow-light mb-5">Company</h3>
            <ul className="space-y-2.5 text-sm">
              {[["About STJ", "/about"], ["Capabilities", "/capabilities"], ["Quality & Engineering", "/quality"],
                ["Industries", "/industries"], ["Global Markets", "/markets"], ["Smart Solutions", "/smart-solutions"],
                ["Knowledge Center", "/knowledge-center"],
                ["Projects", "/projects"], ["Resources", "/resources"],
                ["Insights", "/insights"], ["Contact", "/contact"]].map(([label, to]) => (
                <li key={to}>
                  <Link to={to} data-testid={`footer-link-${to.slice(1)}`} className="transition-colors hover:text-white">{label}</Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-3">
            <h3 className="eyebrow eyebrow-light mb-5">Contact</h3>
            <ul className="space-y-3.5 text-sm">
              <li className="flex items-start gap-3">
                <MapPin size={15} className="mt-0.5 shrink-0 text-accent" />
                <span>
                  {settings.address || "India"}
                  <a href={settings.maps_url || "https://maps.app.goo.gl/3vUp1vknBQThs7i47"}
                    target="_blank" rel="noopener noreferrer" data-testid="footer-maps-link"
                    onClick={() => trackEvent("maps_click")}
                    className="mt-1.5 inline-flex items-center gap-1.5 border border-accent/60 px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.15em] text-accent transition-colors hover:bg-accent hover:text-white">
                    <Navigation size={11} /> Get Directions
                  </a>
                </span>
              </li>
              <li>
                <a href={`mailto:${settings.email || "info@stjinnovations.com"}`} data-testid="footer-email-link"
                  onClick={() => trackEvent("email_click")}
                  className="flex items-center gap-3 transition-colors hover:text-white">
                  <Mail size={15} className="shrink-0 text-accent" />
                  {settings.email || "info@stjinnovations.com"}
                </a>
              </li>
              <li>
                <div className="flex items-start gap-3">
                  <Phone size={15} className="mt-0.5 shrink-0 text-accent" />
                  <div className="space-y-1">
                    {(settings.phones?.length ? settings.phones : [settings.phone].filter(Boolean)).map((p, i) => (
                      <a key={p} href={`tel:${p.replace(/[^+\d]/g, "")}`} data-testid={i === 0 ? "footer-phone-link" : `footer-phone-link-${i + 1}`}
                        onClick={() => trackEvent("phone_click")}
                        className="block transition-colors hover:text-white">
                        {p}
                      </a>
                    ))}
                  </div>
                </div>
              </li>
              <li>
                <a href={`https://wa.me/${(settings.whatsapp || "").replace(/[^0-9]/g, "")}`} data-testid="footer-whatsapp-link"
                  onClick={() => trackEvent("whatsapp_click")} target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-3 transition-colors hover:text-white">
                  <MessageCircle size={15} className="shrink-0 text-accent" />
                  WhatsApp business enquiry
                </a>
              </li>
            </ul>
            <Link to="/request-a-quote" data-testid="footer-rfq-button"
              className="mt-6 inline-flex items-center gap-2 border border-accent px-5 py-2.5 text-[13px] font-semibold text-accent transition-colors hover:bg-accent hover:text-white">
              Request a Quote
            </Link>
          </div>
        </div>

        <div className="mt-14 flex flex-col items-start justify-between gap-4 border-t border-white/10 pt-7 text-xs text-slate-500 sm:flex-row sm:items-center">
          <p>© {new Date().getFullYear()} {SITE.legalName}. All rights reserved.</p>
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            <Link to="/privacy-policy" data-testid="footer-privacy-link" className="hover:text-white">Privacy Policy</Link>
            <Link to="/terms" data-testid="footer-terms-link" className="hover:text-white">Terms</Link>
            <Link to="/cookie-policy" data-testid="footer-cookie-link" className="hover:text-white">Cookie Policy</Link>
            <a href="/api/sitemap.xml" data-testid="footer-sitemap-link" className="hover:text-white">Sitemap</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
